/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive, unwrapReactive } from '@jack-henry/frontend-utils/lit';
import { MessageCenterCategory, MessageCenterService } from '@treasury/domain/message-center';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-date-range';
import { DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const defaultValidState = {
    dateFrom: true,
    dateTo: true,
};

interface DateRange {
    rangeType: DateRangeType;
    startDate?: Date;
    endDate?: Date;
}

const defaultDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

export const tagName = 'message-center-filter-sheet';
@customElement(tagName)
export class MessageCenterFilterSheet extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public open = false;

    @InjectProperty()
    declare private messageCenterService: MessageCenterService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @state()
    private messageCenterCategoryOptions: {
        label: string;
        value: MessageCenterCategory;
    }[] = [];

    @state()
    private messageTypeOptions = [
        { label: 'All', value: 1 },
        { label: 'Inbox', value: 2 },
        { label: 'Sent', value: 3 },
        { label: 'Archived', value: 4 },
    ];

    @state()
    private messageId = '';

    @state()
    private selectedCategory?: MessageCenterCategory;

    @state()
    private selectedMessageType = 1;

    @state()
    private selectedDateRange: DateRange = defaultDateRange;

    @state()
    private loading = false;

    @DeepReactive()
    private validState = defaultValidState;

    protected async firstUpdated() {
        this.loading = true;
        try {
            this.messageCenterCategoryOptions = [
                {
                    label: 'All Categories',
                    value: new MessageCenterCategory({
                        fiMessageCategoryId: 0,
                        categoryDescription: 'All Categories',
                        messageCategory: 'all',
                    }),
                },
                ...(await this.messageCenterService.getMessageCenterCategories()).map(category => ({
                    label: category.messageCategoryDescription || '',
                    value: category,
                })),
            ];
            this.selectedCategory = this.messageCenterCategoryOptions[0].value;
        } catch (e) {
            this.notificationService.renderError(
                (e as Error) || 'Failed to load message categories'
            );
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            .flex=${true}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <span slot="header-center">Search Messages</span>
            ${this.renderFilters()}</tm-bottom-sheet
        >`;
    }

    private renderFilters() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<tm-section class="p-3 mb-10">
                <tm-text-field
                    label="Message ID"
                    .initial=${this.messageId}
                    @value-changed=${(e: CustomEvent) => {
                        this.messageId = e.detail.value;
                    }}
                ></tm-text-field
                ><tm-select
                    label="Category"
                    .items=${this.messageCenterCategoryOptions}
                    .initial=${this.selectedCategory}
                    @value-changed=${(e: CustomEvent) => {
                        if (e.detail.value) {
                            this.selectedCategory = e.detail.value as MessageCenterCategory;
                        }
                    }}
                ></tm-select
                ><tm-date-range
                    label="Date"
                    .initial=${this.selectedDateRange}
                    @value-changed=${(e: CustomEvent) => {
                        const { value, isValid } = e.detail;
                        this.selectedDateRange = value;
                        this.validState.dateFrom = isValid;
                        this.validState.dateTo = isValid;
                    }}
                ></tm-date-range
                ><tm-radio-group
                    label="Type"
                    .options=${this.messageTypeOptions}
                    .initial=${this.selectedMessageType}
                    @value-changed=${(e: CustomEvent) => {
                        this.selectedMessageType = Number(e.detail.value);
                    }}
                ></tm-radio-group> </tm-section
            ><tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(unwrapReactive(this.validState)).some(
                            isValid => !isValid
                        ),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>`;
    }

    private onResetForm() {
        this.selectedCategory = this.messageCenterCategoryOptions[0].value;
        this.messageId = '';
        this.selectedDateRange = defaultDateRange;
        this.selectedMessageType = 1;
        this.validState = {
            ...defaultValidState,
            dateFrom: true,
            dateTo: true,
        };
        this.dispatchEvent(new CustomEvent('reset-filters'));
    }

    private onApplyFilters() {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('apply-filters', {
                detail: this.toDto(),
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
        this.onResetForm();
    }

    private toDto() {
        return {
            categoryId: this.selectedCategory?.fiMessageCategoryId
                ? this.selectedCategory.fiMessageCategoryId
                : undefined,
            messageId: this.messageId,
            dateFrom: this.selectedDateRange.startDate || undefined,
            dateTo:
                this.selectedDateRange.rangeType === 'Today'
                    ? this.selectedDateRange.startDate
                    : this.selectedDateRange.endDate || undefined,
            messageTypeId: Number(this.selectedMessageType),
        };
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: MessageCenterFilterSheet;
    }
}
